import type { FacilitiesSiteView } from '@/lib/api';

/**
 * The building staff page (Warren, 28 Oct 2026, item 5): "They see their
 * location's chargers, live status and alerts, and the steps for a breaker
 * flip or restart; no driver details, no money, no other locations."
 *
 * Pure helpers for pages/facilities/FacilitiesPage.tsx: the wording of a
 * charger's state and an alert, and the steps. The two checks match the
 * offline alert email (packages/notifications charger-offline.ts
 * offlineChecks), so the page and the email say the same thing. No phone
 * number: the way to reach EV Initiative is a reply to the alert email or
 * support@.
 */

export const SUPPORT_EMAIL = 'support@evinitiative.com';

export const OFFLINE_STEPS: readonly string[] = [
  'Check the breaker. Find the circuit breaker that feeds the charger. If it has tripped, switch it fully off, then back on.',
  'Restart the charger. Switch that breaker off, wait 30 seconds, and switch it back on. Give the charger up to 5 minutes to start and reconnect; this page shows it online again on its own. A restart stops any charge in progress.',
  'Check the network. Make sure the site WiFi or ethernet the charger uses is working, including the router or switch it plugs into.',
  `Still offline after that? Reply to the alert email, or write to ${SUPPORT_EMAIL}, and EV Initiative will help.`,
];

export const REFUSED_START_STEPS: readonly string[] = [
  'Restart the charger the same way: its breaker off for 30 seconds, then on.',
  `If drivers still cannot start a charge, reply to the alert email or write to ${SUPPORT_EMAIL}.`,
];

export const SAFETY_NOTE = 'Only switch a breaker you are allowed to. Never open the charger: electrical work needs a licensed electrician.';

type Alert = FacilitiesSiteView['alerts'][number];
type Charger = FacilitiesSiteView['chargers'][number];

/** "3:12 PM" today, else "Oct 27, 3:12 PM", in the site's zone when it has one. */
export function formatWhen(iso: string | null, timeZone: string | null, now: Date = new Date()): string {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const zone = timeZone || undefined;
  const day = (x: Date) => x.toLocaleDateString('en-US', { timeZone: zone, year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { timeZone: zone, hour: 'numeric', minute: '2-digit' });
  return day(d) === day(now) ? time : `${d.toLocaleDateString('en-US', { timeZone: zone, month: 'short', day: 'numeric' })}, ${time}`;
}

export function alertTitle(a: Pick<Alert, 'kind'>): string {
  return a.kind === 'refused_starts' ? 'Refusing starts' : 'Offline';
}

export function alertLine(a: Alert, timeZone: string | null, now: Date = new Date()): string {
  if (a.open) {
    return a.kind === 'refused_starts'
      ? `Drivers' starts are being refused since ${formatWhen(a.detected_at, timeZone, now)}`
      : `Offline since ${formatWhen(a.since, timeZone, now)}`;
  }
  return `Resolved ${formatWhen(a.resolved_at, timeZone, now)}`;
}

/** One word for the charger, and whether something is wrong. */
export function chargerState(c: Charger): { label: string; ok: boolean } {
  if (!c.online) return { label: 'Offline', ok: false };
  const statuses = c.connectors.map((k) => k.status ?? '');
  if (statuses.some((s) => s === 'Faulted')) return { label: 'Fault', ok: false };
  if (statuses.some((s) => s === 'Charging' || s === 'SuspendedEV' || s === 'SuspendedEVSE' || s === 'Finishing')) return { label: 'In use', ok: true };
  if (statuses.length > 0 && statuses.every((s) => s === 'Unavailable')) return { label: 'Unavailable', ok: false };
  return { label: 'Online', ok: true };
}

/** Open alerts first (newest first), then the resolved ones. */
export function orderAlerts(alerts: readonly Alert[]): Alert[] {
  const t = (a: Alert) => new Date(a.detected_at ?? 0).getTime();
  return [...alerts].sort((a, b) => (Number(b.open) - Number(a.open)) || (t(b) - t(a)));
}
